import React, { useEffect, useMemo } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { checkGtmSolutionsLoaded } from '../../support/slices/gtmSolutionsSlice';
import { checkCiscoProductsLoaded } from '../../support/slices/productsSlice';
import { RootState, useAppDispatch } from '../../support/store';
import { useSelector } from 'react-redux';
import { RouteHistoryEntry } from '../../support/slices/uiSlice';
import {
  HbrBreadcrumb,
  HbrBreadcrumbItem,
} from '@harbor/elements-react-standalone';

type RouteType = string | RegExp;

interface BreadcrumbConfigItem {
  route: RegExp | string;
  parentRoutes?: RouteType[];
  defaultParent?: string;
}

const baseRoute = '/solutions';
const productList = `${baseRoute}/product`;
const productDetail = /\/solutions\/product\/\d+$/;
const architectureList = `${baseRoute}/architecture`;
const architectureDetail = /\/solutions\/architecture\/\d+$/;

// Some routes can be accessed in different contexts.
// Breadcrumbs need to reflect user current context.
// This just affects products now but could expand with added views.
const breadcrumbConfig: BreadcrumbConfigItem[] = [
  {
    route: productDetail,
    parentRoutes: [productList, architectureDetail],
  },
  {
    route: architectureDetail,
    parentRoutes: [architectureList],
  },
  {
    route: productList,
    parentRoutes: [baseRoute],
  },
  {
    route: architectureList,
    parentRoutes: [baseRoute],
  }
];

const Solutions = () => {
  const { routeHistory } = useSelector((state: RootState) => state.ui);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(checkGtmSolutionsLoaded());
    dispatch(checkCiscoProductsLoaded());
  }, [dispatch]);

  const compareRoutes = (configRoute: RouteType, historyRoute: string): boolean => {
    if (configRoute instanceof RegExp) {
      return configRoute.test(historyRoute);
    } else {
      return configRoute === historyRoute;
    }
  }

  const getEntryConfig = (entry: RouteHistoryEntry): BreadcrumbConfigItem => {
    return breadcrumbConfig.find(
      (config) => compareRoutes(config.route, entry.route)
    );
  }

  const isParentRoute = (config: BreadcrumbConfigItem, testRoute: RouteHistoryEntry): boolean => {
    if (!config.parentRoutes?.length) {
      return false;
    }
    return config.parentRoutes.some(route => compareRoutes(route, testRoute.route));
  }

  const breadcrumbList: RouteHistoryEntry[] = useMemo(() => {
    const solutionsEntries = routeHistory.filter(r => r.route.startsWith(baseRoute)).reverse();
    const breadcrumbs: RouteHistoryEntry[] = [solutionsEntries[0] ?? {route: baseRoute, params: {}}];
    let counter = 1;
    while (counter < solutionsEntries.length) {
      const currentConfig = getEntryConfig(breadcrumbs[0]);
      if (!currentConfig) {
        break;
      }
      let parent: RouteHistoryEntry;
      let idx = 0;
      for (const entry of solutionsEntries.slice(counter)) {
        if(isParentRoute(currentConfig, entry)) {
          counter = counter + idx++;
          parent = entry;
          break;
        }
      }
      if (parent) {
        breadcrumbs.unshift(parent);
      } else if (currentConfig.parentRoutes?.length) {
        breadcrumbs.unshift({route: currentConfig.parentRoutes[0].toString(), params: {}});
        counter++;
      } else {
        counter++;
      }
    }
    if (breadcrumbs[0].route !== baseRoute) {
      breadcrumbs.unshift({route: baseRoute, params: {}});
    }
    return breadcrumbs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeHistory]);

  return (
    <>
      <section className='mx-4 mt-2'>
        <HbrBreadcrumb>
          {breadcrumbList.map((breadcrumb, i) => (
            <HbrBreadcrumbItem key={`breadcrumb-${i}`}>
              <Link to={breadcrumb.route} className='capitalize'>
                {
                  breadcrumb.title 
                  ?? breadcrumb.params.section 
                  ?? breadcrumb.route.replace('/', ' ')
                }
              </Link>
            </HbrBreadcrumbItem>
          ))}
        </HbrBreadcrumb>
      </section>
      <Outlet />
    </>
  );
};

export default Solutions;
